import { createContext, useContext, type ReactNode } from 'react';
import { translate, type UiKey } from './i18n';
import type { Lang, MediaRef, MenuNode, PageDto, ResourcesDto, SiteDto, SliderDto } from './types';

export interface RenderContextValue {
  lang: Lang;
  page?: PageDto | null;
  site?: SiteDto | null;
  menus: Record<string, MenuNode[]>;
  resources: ResourcesDto;
  /** 'public' on the visitor site, 'editor' inside the admin canvas (no tracking, no autoplay side effects). */
  mode: 'public' | 'editor';
  apiBase: string;
  /** Extra client-side sanitizing (the admin preview renders unsaved rich text). */
  sanitizeHtml?: (html: string) => string;
}

const emptyResources: ResourcesDto = { media: {}, sliders: {}, links: {}, maps: {} };

const RenderContext = createContext<RenderContextValue>({
  lang: 'de',
  menus: {},
  resources: emptyResources,
  mode: 'public',
  apiBase: '',
});

export function RenderProvider({ value, children }: { value: RenderContextValue; children: ReactNode }) {
  return <RenderContext.Provider value={value}>{children}</RenderContext.Provider>;
}

export function useRender() {
  return useContext(RenderContext);
}

export function useT() {
  const { lang } = useRender();
  return (key: UiKey, vars?: Record<string, string | number>) => translate(lang, key, vars);
}

export function useMedia(id?: string | null): MediaRef | undefined {
  const { resources } = useRender();
  return id ? resources.media[id] : undefined;
}

export function useSlider(id?: string | null): SliderDto | undefined {
  const { resources } = useRender();
  return id ? resources.sliders[id] : undefined;
}

/**
 * Resolves stored hrefs. "page:{id}" becomes the page URL in the current language, or null when that page is not
 * published in this language (renderers then show plain text instead of a dead link).
 */
export function useHref() {
  const { resources } = useRender();
  return (href?: string | null): string | null => {
    if (!href) return null;
    if (href.startsWith('page:')) return resources.links[href] ?? null;
    return href;
  };
}

/** Id of the block being rendered (used by blocks whose server-side resources are keyed by block, e.g. maps). */
const BlockIdContext = createContext<string | undefined>(undefined);
export const BlockIdProvider = BlockIdContext.Provider;
export function useBlockId() {
  return useContext(BlockIdContext);
}

export function isExternal(href: string) {
  return /^https?:\/\//i.test(href);
}
